// Calendar executor input boundary. The authenticated handler owns user,
// connection, quota, confirmation and receipt validation; providers own IO.
export type CalendarProvider = 'google_calendar' | 'outlook';
export type CalendarReadAction = 'list_events' | 'get_event' | 'availability';
export type CalendarWriteAction = 'create_event' | 'update_event' | 'delete_event';
export type CalendarAction = CalendarReadAction | CalendarWriteAction;
export type CalendarEventScope = 'single' | 'occurrence' | 'series';
export interface CalendarRecurrence { frequency: 'daily' | 'weekly' | 'monthly' | 'yearly'; interval: number; count: number }
export interface CalendarEventOptions { attendees: { email: string }[]; recurrence: CalendarRecurrence | null; scope: CalendarEventScope; notify_attendees: boolean }
export interface CalendarDraft {
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  time_zone: string;
  description: string;
  location: string;
  event_options?: CalendarEventOptions;
}
export type CalendarRequest =
  | { provider: CalendarProvider; action: 'list_events' | 'availability'; time_min: string; time_max: string; limit: number; query: string }
  | { provider: CalendarProvider; action: 'get_event'; event_id: string }
  | ({ provider: CalendarProvider; action: 'create_event' } & CalendarDraft)
  | ({ provider: CalendarProvider; action: 'update_event'; event_id: string; etag: string } & CalendarDraft)
  | { provider: CalendarProvider; action: 'delete_event'; event_id: string; etag: string; event_options?: CalendarEventOptions };
export interface CalendarEvent extends CalendarDraft {
  id: string;
  etag: string;
  busy: boolean;
  recurring: boolean;
  attendee_count: number;
  writable: boolean;
  content_truncated?: boolean;
  write_restriction?: string;
  advanced_writable?: boolean;
  series_id?: string;
}
export const CALENDAR_PROVIDERS = ['google_calendar', 'outlook'] as const;
export const CALENDAR_ACTIONS = ['list_events', 'get_event', 'availability', 'create_event', 'update_event', 'delete_event'] as const;
export const CALENDAR_MAX_WINDOW_DAYS = 31;
export function isCalendarWriteAction(action: string): action is CalendarWriteAction {
  return action === 'create_event' || action === 'update_event' || action === 'delete_event';
}
export function calendarObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
export function isCalendarTimeZone(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 100 || !/^[A-Za-z0-9_+\-/]+$/.test(value)) return false;
  try { new Intl.DateTimeFormat('en', { timeZone: value }).format(0); return true; } catch { return false; }
}
export function calendarDate(value: unknown): string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('invalid_calendar_date');
  const date = new Date(value + 'T00:00:00.000Z');
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) throw new Error('invalid_calendar_date');
  return value;
}
export function calendarUtc(value: unknown): string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(value)) throw new Error('invalid_calendar_time');
  const datePart = calendarDate(value.slice(0, 10));
  if (Number(value.slice(11, 13)) > 23 || Number(value.slice(14, 16)) > 59 || Number(value.slice(17, 19)) > 59) throw new Error('invalid_calendar_time');
  const date = new Date(value);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== datePart) throw new Error('invalid_calendar_time');
  return date.toISOString();
}
function text(value: unknown, key: string, max: number, required = false): string {
  if (value === undefined && !required) return '';
  if (typeof value !== 'string' || value.length > max || [...value].some(char => char.charCodeAt(0) === 127 || (char.charCodeAt(0) < 32 && ![9, 10, 13].includes(char.charCodeAt(0))))) throw new Error('invalid_calendar_' + key);
  const normalized = value.trim();
  if (required && !normalized) throw new Error('invalid_calendar_' + key);
  return normalized;
}
export function calendarEventId(value: unknown): string {
  if (typeof value !== 'string' || !value || value === '.' || value === '..' || value.length > 1500 || value.trim() !== value || [...value].some(char => char.charCodeAt(0) <= 32 || char.charCodeAt(0) === 127)) throw new Error('invalid_calendar_event_id');
  return value;
}
export function calendarEtag(value: unknown): string {
  // Neither wildcard writes nor a fabricated unquoted version can overwrite
  // a changed event. Real Google and Graph event ETags are quoted strings.
  if (typeof value !== 'string' || value.length > 1000 || !/^(?:W\/)?"[^"]+"$/.test(value) || [...value].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) throw new Error('invalid_calendar_etag');
  return value;
}
export function parseCalendarRequest(value: unknown): CalendarRequest {
  if (!calendarObject(value)) throw new Error('invalid_calendar_request');
  if (!CALENDAR_PROVIDERS.includes(value.provider as CalendarProvider)) throw new Error('unsupported_calendar_provider');
  if (!CALENDAR_ACTIONS.includes(value.action as CalendarAction)) throw new Error('unsupported_calendar_action');
  const provider = value.provider as CalendarProvider;
  const action = value.action as CalendarAction;
  const transport = ['provider', 'action', 'confirmed', 'idempotency_key', 'mirror_context'];
  const readKeys = action === 'get_event' ? ['event_id'] : ['time_min', 'time_max', 'limit', 'query'];
  const writeKeys = action === 'delete_event' ? ['event_id', 'etag', 'event_options'] : ['title', 'start', 'end', 'all_day', 'time_zone', 'description', 'location', 'event_options', ...(action === 'update_event' ? ['event_id', 'etag'] : [])];
  if (['attendees', 'invitations', 'recurrence', 'recurring', 'conferenceData', 'isOnlineMeeting', 'online_meeting', 'sendUpdates'].some(key => key in value)) throw new Error('calendar_feature_unsupported');
  if (Object.keys(value).some(key => !transport.includes(key) && !(isCalendarWriteAction(action) ? writeKeys : readKeys).includes(key))) throw new Error('invalid_calendar_fields');
  if (action === 'get_event') return { provider, action, event_id: calendarEventId(value.event_id) };
  if (action === 'list_events' || action === 'availability') {
    const time_min = calendarUtc(value.time_min), time_max = calendarUtc(value.time_max);
    const span = Date.parse(time_max) - Date.parse(time_min);
    if (span <= 0 || span > CALENDAR_MAX_WINDOW_DAYS * 86_400_000) throw new Error('invalid_calendar_window');
    const limit = value.limit === undefined ? 10 : value.limit;
    if (typeof limit !== 'number' || !Number.isInteger(limit) || limit < 1 || limit > 10) throw new Error('invalid_calendar_limit');
    const query = text(value.query, 'query', 200);
    if (action === 'availability' && query) throw new Error('availability_query_unsupported');
    return { provider, action, time_min, time_max, limit, query };
  }
  if (action === 'delete_event') return { provider, action, event_id: calendarEventId(value.event_id), etag: calendarEtag(value.etag), ...(value.event_options !== undefined ? { event_options: parseCalendarEventOptions(value.event_options) } : {}) };
  if (typeof value.all_day !== 'boolean') throw new Error('invalid_calendar_all_day');
  if (!isCalendarTimeZone(value.time_zone)) throw new Error('invalid_calendar_time_zone');
  const start = value.all_day ? calendarDate(value.start) : calendarUtc(value.start);
  const end = value.all_day ? calendarDate(value.end) : calendarUtc(value.end);
  const span = Date.parse(end) - Date.parse(start);
  if (span <= 0 || span > (value.all_day ? 366 : CALENDAR_MAX_WINDOW_DAYS) * 86_400_000) throw new Error('invalid_calendar_duration');
  const draft: CalendarDraft = { title: text(value.title, 'title', 300, true), start, end, all_day: value.all_day,
    time_zone: value.time_zone, description: text(value.description, 'description', 8000), location: text(value.location, 'location', 1000) };
  if (value.event_options !== undefined) {
    draft.event_options = parseCalendarEventOptions(value.event_options);
    if (action === 'create_event' && (draft.event_options.scope === 'occurrence' || (draft.event_options.recurrence !== null) !== (draft.event_options.scope === 'series'))) throw new Error('invalid_calendar_scope');
  }
  return action === 'create_event' ? { provider, action, ...draft } : { provider, action: 'update_event', event_id: calendarEventId(value.event_id), etag: calendarEtag(value.etag), ...draft };
}
/** Advanced fields are a reviewed, closed envelope. Legacy top-level provider
 * fields remain refused: neither model output nor an arbitrary API URL is a writer. */
export function parseCalendarRecurrence(value: unknown): CalendarRecurrence | null {
  if (value === null) return null;
  if (!calendarObject(value) || Object.keys(value).sort().join(',') !== 'count,frequency,interval' ||
      !['daily','weekly','monthly','yearly'].includes(String(value.frequency)) ||
      !Number.isInteger(value.interval) || Number(value.interval) < 1 || Number(value.interval) > 12 ||
      !Number.isInteger(value.count) || Number(value.count) < 2 || Number(value.count) > 100) {
    throw new Error('invalid_calendar_recurrence');
  }
  return { frequency: value.frequency as CalendarRecurrence['frequency'], interval: Number(value.interval), count: Number(value.count) };
}
export function calendarAttendeeEmail(value: unknown): string {
  if (typeof value !== 'string' || value.length > 254 || value.trim() !== value ||
      !/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/.test(value)) {
    throw new Error('invalid_calendar_attendee');
  }
  const local = value.split('@')[0];
  if (local.length > 64 || local.startsWith('.') || local.endsWith('.') || local.includes('..')) throw new Error('invalid_calendar_attendee');
  return value;
}
export function parseCalendarEventOptions(value: unknown, requireConsent = true): CalendarEventOptions {
  if (!calendarObject(value) || Object.keys(value).sort().join(',') !== 'attendees,notify_attendees,recurrence,scope' ||
      !Array.isArray(value.attendees) || value.attendees.length > 20 ||
      !['single','occurrence','series'].includes(String(value.scope)) || typeof value.notify_attendees !== 'boolean') {
    throw new Error('invalid_calendar_event_options');
  }
  const attendees = value.attendees.map(row => {
    if (!calendarObject(row) || Object.keys(row).join(',') !== 'email') throw new Error('invalid_calendar_attendee');
    return { email: calendarAttendeeEmail(row.email) };
  });
  if (new Set(attendees.map(row => row.email.toLowerCase())).size !== attendees.length) throw new Error('duplicate_calendar_attendee');
  if (requireConsent && attendees.length > 0 && value.notify_attendees !== true) throw new Error('calendar_notification_consent_required');
  const recurrence = parseCalendarRecurrence(value.recurrence);
  if (value.scope !== 'series' && recurrence !== null) throw new Error('invalid_calendar_scope');
  return { attendees, recurrence, scope: value.scope as CalendarEventScope, notify_attendees: value.notify_attendees };
}
export function calendarCivilStart(draft: CalendarDraft): string {
  if (draft.all_day) return calendarDate(draft.start);
  const parts = new Intl.DateTimeFormat('en', { timeZone: draft.time_zone, year:'numeric', month:'2-digit', day:'2-digit' }).formatToParts(new Date(draft.start));
  const part = (name: string) => parts.find(row => row.type === name)?.value || '';
  return calendarDate(part('year') + '-' + part('month') + '-' + part('day'));
}